import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MdPerson, MdLogout, MdKeyboardArrowDown, MdReceipt, MdSettings, MdHelp, MdHome, MdPoll, MdListAlt, MdPeople, MdAssessment, MdFavorite, MdGavel } from 'react-icons/md';
import NotificationDropdown from './NotificationDropdown';

function NavBar() {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { logout, user } = useAuth();
  const location = useLocation();
  const menuRef = useRef(null);

  // Check if user is from the United States
  const isUSUser = user?.country === 'US' || user?.country === 'United States' || user?.country === 'USA';

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        {/* Left side: Logo */}
        <div className="flex items-center">
          <Link to="/home" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_votesphere-6/artifacts/suvvb8kr_App%20icon.png" 
              alt="CIVOTE" 
              className="h-10 w-10 rounded-lg"
            />
          </Link>
        </div>
        
        {/* Center: Navigation Links */}
        <div className="flex items-center space-x-1">
          <Link 
            to="/home" 
            className={`flex items-center gap-1.5 px-3 py-2 rounded-card text-sm font-medium font-rubik transition-all ${
              isActive('/home') 
                ? 'bg-primary text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="nav-home-button"
            title="Home"
          >
            <MdHome className="text-lg" />
            <span className="hidden md:inline">Home</span>
          </Link>
          <Link 
            to="/polls" 
            className={`flex items-center gap-1.5 px-3 py-2 rounded-card text-sm font-medium font-rubik transition-all ${
              isActive('/polls') 
                ? 'bg-primary text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="nav-all-polls-button"
            title="All Polls"
          >
            <MdPoll className="text-lg" />
            <span className="hidden md:inline">All Polls</span>
          </Link>
          <Link 
            to="/my-polls" 
            className={`flex items-center gap-1.5 px-3 py-2 rounded-card text-sm font-medium font-rubik transition-all ${
              isActive('/my-polls') 
                ? 'bg-primary text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="nav-my-polls-button"
            title="My Polls"
          >
            <MdListAlt className="text-lg" />
            <span className="hidden md:inline">My Polls</span>
          </Link>
          {isUSUser && (
            <Link 
              to="/reps" 
              className={`flex items-center gap-1.5 px-3 py-2 rounded-card text-sm font-medium font-rubik transition-all ${
                isActive('/reps') || isActive('/offices') || isActive('/representatives') || location.pathname.startsWith('/representatives/')
                  ? 'bg-primary text-white' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="nav-reps-button"
              title="Reps"
            >
              <MdPeople className="text-lg" />
              <span className="hidden md:inline">Reps</span>
            </Link>
          )}
          <Link 
            to="/bills" 
            className={`flex items-center gap-1.5 px-3 py-2 rounded-card text-sm font-medium font-rubik transition-all ${
              isActive('/bills') 
                ? 'bg-primary text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="nav-bills-button"
            title="Bills"
          >
            <MdGavel className="text-lg" />
            <span className="hidden md:inline">Bills</span>
          </Link>
          <Link 
            to="/favorites" 
            className={`flex items-center gap-1.5 px-3 py-2 rounded-card text-sm font-medium font-rubik transition-all ${
              isActive('/favorites') 
                ? 'bg-primary text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="nav-favorites-button"
            title="Favorites"
          >
            <MdFavorite className="text-lg" />
            <span className="hidden md:inline">Favorites</span>
          </Link>
        </div>

        {/* Right side: Notifications + User Menu */}
        <div className="flex items-center gap-2">
          <NotificationDropdown />
          <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 px-3 py-2 rounded-card bg-gray-100 hover:bg-gray-200 transition-all"
            data-testid="user-menu-button"
          >
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <MdPerson className="text-white text-lg" />
            </div>
            <MdKeyboardArrowDown className={`text-gray-500 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-card shadow-lg border border-gray-200 py-2 z-50">
              <Link
                to="/profile"
                onClick={() => setShowUserMenu(false)}
                className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors font-rubik text-sm"
                data-testid="user-menu-profile"
              >
                <MdPerson className="text-lg text-gray-500" />
                Profile
              </Link>
              <Link
                to="/transactions"
                onClick={() => setShowUserMenu(false)}
                className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors font-rubik text-sm"
                data-testid="user-menu-transactions"
              >
                <MdReceipt className="text-lg text-gray-500" />
                Transactions
              </Link>
              <Link
                to="/transactions?tab=candidacy"
                onClick={() => setShowUserMenu(false)}
                className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors font-rubik text-sm"
                data-testid="user-menu-candidacy"
              >
                <MdAssessment className="text-lg text-tertiary" />
                Candidacy Assessment
              </Link>
              <Link
                to="/account-settings"
                onClick={() => setShowUserMenu(false)}
                className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors font-rubik text-sm"
                data-testid="user-menu-account-settings"
              >
                <MdSettings className="text-lg text-gray-500" />
                Account Settings
              </Link>
              <Link
                to="/help"
                onClick={() => setShowUserMenu(false)}
                className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors font-rubik text-sm"
                data-testid="user-menu-help"
              >
                <MdHelp className="text-lg text-gray-500" />
                Help & Support
              </Link>
              <hr className="my-1 border-gray-200" />
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  logout();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-danger hover:bg-red-50 transition-colors font-rubik text-sm"
                data-testid="user-menu-logout"
              >
                <MdLogout className="text-lg" />
                Logout
              </button>
            </div>
          )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default NavBar;
